import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { isAndroidNativeGoogle, signInWithGoogleNative } from '../services/googleAuth';

import Loading from './Loading';
const GOOGLE_AUTH_TIMEOUT_MS = 15000;

export default function GoogleSignInButton({ mode = 'login', referralCode = '', promoCode = '', onSuccess, onError }) {
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const inFlightRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [webComponent, setWebComponent] = useState(null);

  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Authoritative Capacitor platform boundary. Web GIS is lazy-loaded only on web.
  useEffect(() => {
    let cancelled = false;
    if (isAndroidNativeGoogle()) {
      console.info('[9jaWin GoogleAuth] platform=android selected_path=native');
      setWebComponent(null);
      setLoading(false);
      return undefined;
    }
    import('./WebGoogleSignInButton').then((module) => {
      if (!cancelled) setWebComponent(() => module.default);
    }).catch((error) => {
      if (!cancelled) { setLocalError(error?.message || 'Unable to load Google Sign-In.'); onErrorRef.current?.(error?.message); }
    });
    return () => { cancelled = true; };
  }, []);

  const authenticateCredential = async (credential) => {
    if (inFlightRef.current) return;
    try {
      inFlightRef.current = true;
      setLoading(true);
      console.info('[9jaWin GoogleAuth] backend authentication started mode=%s', mode);
      const request = {
        credential, mode,
        referralCode: mode === 'register' ? referralCode || undefined : undefined,
        promoCode: mode === 'register' ? promoCode || undefined : undefined,
      };
      const result = mode === 'link'
        ? await api.auth.linkGoogle(request, { timeout: GOOGLE_AUTH_TIMEOUT_MS })
        : await api.auth.google(request, { timeout: GOOGLE_AUTH_TIMEOUT_MS });
      console.info('[9jaWin GoogleAuth] backend authentication success');
      onSuccessRef.current?.(mode === 'link' ? result.data : result.data?.data?.pendingGoogleSignup ? result.data : result.data?.data);
    } catch (error) {
      const unauthenticatedLink = mode === 'link' && error.response?.headers?.['x-auth-required'] === 'true';
      const message = unauthenticatedLink ? 'You must be signed in to your 9jaWin account before linking Google.'
        : error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' ? 'Google authentication timed out. Please try again.'
        : error.response?.data?.message || 'Google authentication failed. Please try again.';
      console.error('[9jaWin GoogleAuth] backend authentication failure');
      setLocalError(message); onErrorRef.current?.(message);
    } finally { inFlightRef.current = false; setLoading(false); }
  };

  const handleNative = async () => {
    if (inFlightRef.current) return;
    setLocalError(''); setLoading(true);
    try {
      const idToken = await signInWithGoogleNative();
      await authenticateCredential(idToken);
    } catch (error) {
      if (error?.code === 'GOOGLE_CANCELLED') { setLoading(false); return; }
      const message = error?.message || 'Native Google Sign-In failed. Please try again.';
      setLocalError(message); onErrorRef.current?.(message); setLoading(false);
    }
  };

  if (isAndroidNativeGoogle()) {
    return <div style={{ marginTop: 20, marginBottom: 20, width: '100%' }}>
      <button type="button" onClick={handleNative} disabled={loading}
        aria-label={mode === 'register' ? 'Continue with Google to create an account' : 'Continue with Google to sign in'}
        style={{ width: '100%', minHeight: 44, borderRadius: 4, border: '1px solid #dadce0', background: '#fff', color: '#3c4043', fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
        {loading ? 'Signing in with Google…' : 'Sign in with Google'}
      </button>
      {localError && <div role="alert" style={{ marginTop: 8, color: '#ff4d4d', fontSize: 13, textAlign: 'center' }}>{localError}</div>}
    </div>;
  }

  const WebButton = webComponent;
  return <div style={{ marginTop: 20, marginBottom: 20, width: '100%' }}>
    {WebButton ? <WebButton mode={mode} onCredential={authenticateCredential} onError={(message) => { setLocalError(message); onErrorRef.current?.(message); }} /> : <Loading variant="section" label="" />}
    {localError && !WebButton && <div role="alert" style={{ marginTop: 8, color: '#ff4d4d', fontSize: 13, textAlign: 'center' }}>{localError}</div>}
  </div>;
}
